/**
 * Product Design Editor
 * Vistaprint-style editor with drag, resize, rotate, and text features
 */
const ProductEditor = {
  // Configuration
  config: {
    maxFileSize: 5 * 1024 * 1024,
    allowedTypes: ['image/png', 'image/jpeg', 'image/svg+xml'],
    fonts: [
      { name: 'Arial', family: 'Arial, sans-serif' },
      { name: 'Helvetica', family: 'Helvetica, sans-serif' },
      { name: 'Georgia', family: 'Georgia, serif' },
      { name: 'Times New Roman', family: '"Times New Roman", serif' },
      { name: 'Verdana', family: 'Verdana, sans-serif' },
      { name: 'Impact', family: 'Impact, sans-serif' },
      { name: 'Comic Sans', family: '"Comic Sans MS", cursive' },
      { name: 'Courier', family: '"Courier New", monospace' }
    ],
    cloudinary: {
      cloudName: '',
      uploadPreset: ''
    }
  },

  // State
  state: {
    currentView: 'front',
    elements: [], // Array of design elements (logos, text)
    selectedElement: null,
    isDragging: false,
    isResizing: false,
    isRotating: false,
    dragStart: { x: 0, y: 0 },
    elementStart: { x: 0, y: 0, width: 0, height: 0, rotation: 0 },
    // Background removal state
    bgRemoval: {
      originalDataUrl: null,
      resultDataUrl: null,
      isProcessing: false,
      pendingFile: null
    }
  },

  /**
   * Initialize the editor
   */
  init() {
    const editorCanvas = document.querySelector('[data-editor-canvas]');
    if (!editorCanvas) return;

    this.loadConfig();
    this.cacheElements();
    this.bindEvents();
    this.initializeViews();
  },

  loadConfig() {
    const configEl = document.querySelector('[data-customizer-config]');
    if (configEl) {
      this.config.cloudinary.cloudName = configEl.dataset.cloudName || '';
      this.config.cloudinary.uploadPreset = configEl.dataset.uploadPreset || '';
    }
  },

  cacheElements() {
    this.dom = {
      editorCanvas: document.querySelector('[data-editor-canvas]'),
      productImage: document.querySelector('[data-product-image]'),
      designLayer: document.querySelector('[data-design-layer]'),
      viewTabs: document.querySelectorAll('[data-view-tab]'),
      uploadBtn: document.querySelector('[data-upload-btn]'),
      uploadInput: document.querySelector('[data-upload-input]'),
      addTextBtn: document.querySelector('[data-add-text-btn]'),
      toolbar: document.querySelector('[data-editor-toolbar]'),
      deleteBtn: document.querySelector('[data-delete-element]'),
      duplicateBtn: document.querySelector('[data-duplicate-element]'),
      bringForwardBtn: document.querySelector('[data-bring-forward]'),
      sendBackwardBtn: document.querySelector('[data-send-backward]'),
      fontSelect: document.querySelector('[data-font-select]'),
      fontSizeInput: document.querySelector('[data-font-size]'),
      textColorInput: document.querySelector('[data-text-color]'),
      textInput: document.querySelector('[data-text-input]'),
      form: document.querySelector('[data-product-form]'),
      designDataInput: document.querySelector('[name="properties[_design_data]"]'),
      // Background removal elements
      bgRemovalPanel: document.querySelector('[data-bg-removal-panel]'),
      bgPreviewOriginal: document.querySelector('[data-bg-preview-original]'),
      bgPreviewResult: document.querySelector('[data-bg-preview-result]'),
      bgRemoveBtn: document.querySelector('[data-remove-bg-btn]'),
      bgUseOriginalBtn: document.querySelector('[data-use-original-btn]'),
      bgProcessing: document.querySelector('[data-bg-processing]'),
      bgResultActions: document.querySelector('[data-bg-result-actions]'),
      bgUseResultBtn: document.querySelector('[data-use-result-btn]'),
      bgDownloadResultBtn: document.querySelector('[data-download-result-btn]'),
      bgCloseBtn: document.querySelector('[data-bg-removal-close]')
    };
  },

  bindEvents() {
    // Upload events
    this.dom.uploadBtn?.addEventListener('click', () => this.dom.uploadInput?.click());
    this.dom.uploadInput?.addEventListener('change', (e) => this.handleFileUpload(e));

    // Add text
    this.dom.addTextBtn?.addEventListener('click', () => this.addTextElement());

    // View tabs
    this.dom.viewTabs?.forEach(tab => {
      tab.addEventListener('click', (e) => this.switchView(e.currentTarget.dataset.view));
    });

    // Canvas events for drag/resize/rotate
    this.dom.editorCanvas?.addEventListener('mousedown', (e) => this.handleMouseDown(e));
    document.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    document.addEventListener('mouseup', (e) => this.handleMouseUp(e));

    // Touch events for mobile
    this.dom.editorCanvas?.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: false });
    document.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false });
    document.addEventListener('touchend', (e) => this.handleTouchEnd(e));

    // Toolbar actions
    this.dom.deleteBtn?.addEventListener('click', () => this.deleteSelectedElement());
    this.dom.duplicateBtn?.addEventListener('click', () => this.duplicateSelectedElement());
    this.dom.bringForwardBtn?.addEventListener('click', () => this.bringForward());
    this.dom.sendBackwardBtn?.addEventListener('click', () => this.sendBackward());

    // Text editing
    this.dom.fontSelect?.addEventListener('change', (e) => this.updateTextFont(e.target.value));
    this.dom.fontSizeInput?.addEventListener('input', (e) => this.updateTextSize(e.target.value));
    this.dom.textColorInput?.addEventListener('input', (e) => this.updateTextColor(e.target.value));
    this.dom.textInput?.addEventListener('input', (e) => this.updateTextContent(e.target.value));

    // Click outside to deselect
    this.dom.editorCanvas?.addEventListener('click', (e) => {
      if (e.target === this.dom.designLayer || e.target === this.dom.editorCanvas) {
        this.deselectAll();
      }
    });

    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => this.handleKeyDown(e));

    // Form submission
    this.dom.form?.addEventListener('submit', (e) => this.handleFormSubmit(e));

    // Background removal events
    this.dom.bgRemoveBtn?.addEventListener('click', () => this.removeBackground());
    this.dom.bgUseOriginalBtn?.addEventListener('click', () => this.useOriginalImage());
    this.dom.bgUseResultBtn?.addEventListener('click', () => this.useResultImage());
    this.dom.bgDownloadResultBtn?.addEventListener('click', () => this.downloadResultImage());
    this.dom.bgCloseBtn?.addEventListener('click', () => this.closeBgRemovalPanel());
  },

  initializeViews() {
    // Initialize font select options
    if (this.dom.fontSelect) {
      this.dom.fontSelect.innerHTML = this.config.fonts
        .map(f => `<option value="${f.family}" style="font-family: ${f.family}">${f.name}</option>`)
        .join('');
    }
  },

  // =========================================================================
  // View Management
  // =========================================================================

  switchView(view) {
    this.state.currentView = view;

    // Update tabs
    this.dom.viewTabs?.forEach(tab => {
      tab.classList.toggle('active', tab.dataset.view === view);
    });

    // Update product images
    document.querySelectorAll('[data-product-view]').forEach(img => {
      img.classList.toggle('active', img.dataset.productView === view);
    });

    // Show/hide elements for this view
    this.state.elements.forEach(el => {
      if (el.dom) {
        el.dom.style.display = el.view === view ? 'block' : 'none';
      }
    });

    this.deselectAll();
  },

  // =========================================================================
  // File Upload
  // =========================================================================

  async handleFileUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate
    if (!this.config.allowedTypes.includes(file.type)) {
      alert('Please upload a PNG, JPG, or SVG file.');
      return;
    }
    if (file.size > this.config.maxFileSize) {
      alert('File size must be under 5MB.');
      return;
    }

    try {
      const dataUrl = await this.readFileAsDataUrl(file);

      // Show background removal panel for images (not SVGs)
      if (file.type !== 'image/svg+xml' && this.dom.bgRemovalPanel) {
        this.showBgRemovalPanel(dataUrl, file);
      } else {
        // For SVGs, add directly without background removal option
        this.addImageElement(dataUrl, file.name);
        this.uploadToCloudIfConfigured(file);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image. Please try again.');
    }

    // Reset input
    e.target.value = '';
  },

  async uploadToCloudIfConfigured(file) {
    if (this.config.cloudinary.cloudName && this.config.cloudinary.uploadPreset) {
      try {
        const cloudUrl = await this.uploadToCloudinary(file);
        const element = this.state.elements[this.state.elements.length - 1];
        if (element) element.cloudUrl = cloudUrl;
      } catch (error) {
        console.error('Cloud upload error:', error);
      }
    }
  },

  readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  },

  async uploadToCloudinary(file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', this.config.cloudinary.uploadPreset);

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${this.config.cloudinary.cloudName}/image/upload`,
      { method: 'POST', body: formData }
    );

    if (!response.ok) throw new Error('Upload failed');
    const data = await response.json();
    return data.secure_url;
  },

  // =========================================================================
  // Element Creation
  // =========================================================================

  addImageElement(src, filename = 'logo') {
    const canvasRect = this.dom.designLayer.getBoundingClientRect();
    const element = {
      id: 'el_' + Date.now(),
      type: 'image',
      view: this.state.currentView,
      src: src,
      filename: filename,
      x: 50, // percentage
      y: 50,
      width: 30, // percentage of canvas
      height: 'auto',
      rotation: 0,
      zIndex: this.state.elements.length + 1
    };

    this.state.elements.push(element);
    this.renderElement(element);
    this.selectElement(element);
  },

  addTextElement() {
    const element = {
      id: 'el_' + Date.now(),
      type: 'text',
      view: this.state.currentView,
      text: 'Your Text',
      fontFamily: this.config.fonts[0].family,
      fontSize: 24,
      color: '#000000',
      x: 50,
      y: 60,
      width: 'auto',
      height: 'auto',
      rotation: 0,
      zIndex: this.state.elements.length + 1
    };

    this.state.elements.push(element);
    this.renderElement(element);
    this.selectElement(element);

    // Update toolbar inputs
    if (this.dom.textInput) this.dom.textInput.value = element.text;
    if (this.dom.fontSelect) this.dom.fontSelect.value = element.fontFamily;
    if (this.dom.fontSizeInput) this.dom.fontSizeInput.value = element.fontSize;
    if (this.dom.textColorInput) this.dom.textColorInput.value = element.color;
  },

  renderElement(element) {
    const wrapper = document.createElement('div');
    wrapper.className = 'design-element';
    wrapper.dataset.elementId = element.id;
    wrapper.style.cssText = `
      position: absolute;
      left: ${element.x}%;
      top: ${element.y}%;
      transform: translate(-50%, -50%) rotate(${element.rotation}deg);
      z-index: ${element.zIndex};
      cursor: move;
      display: ${element.view === this.state.currentView ? 'block' : 'none'};
    `;

    if (element.type === 'image') {
      const img = document.createElement('img');
      img.src = element.src;
      img.style.cssText = `
        width: ${element.width}%;
        max-width: 80%;
        height: auto;
        pointer-events: none;
        user-select: none;
      `;
      img.draggable = false;
      wrapper.appendChild(img);

      // Set width after image loads
      img.onload = () => {
        const canvasWidth = this.dom.designLayer.offsetWidth;
        const imgWidth = (element.width / 100) * canvasWidth;
        img.style.width = `${imgWidth}px`;
        element.actualWidth = img.offsetWidth;
        element.actualHeight = img.offsetHeight;
      };
    } else if (element.type === 'text') {
      const textEl = document.createElement('div');
      textEl.className = 'design-text';
      textEl.textContent = element.text;
      textEl.style.cssText = `
        font-family: ${element.fontFamily};
        font-size: ${element.fontSize}px;
        color: ${element.color};
        white-space: nowrap;
        pointer-events: none;
        user-select: none;
      `;
      wrapper.appendChild(textEl);
    }

    // Add resize handles
    const handles = ['nw', 'ne', 'se', 'sw'];
    handles.forEach(pos => {
      const handle = document.createElement('div');
      handle.className = `resize-handle resize-handle-${pos}`;
      handle.dataset.handle = pos;
      wrapper.appendChild(handle);
    });

    // Add rotation handle
    const rotateHandle = document.createElement('div');
    rotateHandle.className = 'rotate-handle';
    rotateHandle.dataset.rotate = 'true';
    wrapper.appendChild(rotateHandle);

    this.dom.designLayer.appendChild(wrapper);
    element.dom = wrapper;
  },

  // =========================================================================
  // Selection
  // =========================================================================

  selectElement(element) {
    this.deselectAll();
    this.state.selectedElement = element;
    element.dom?.classList.add('selected');
    this.showToolbar(element);
  },

  deselectAll() {
    this.state.selectedElement = null;
    document.querySelectorAll('.design-element').forEach(el => {
      el.classList.remove('selected');
    });
    this.hideToolbar();
  },

  showToolbar(element) {
    if (!this.dom.toolbar) return;

    this.dom.toolbar.classList.add('visible');

    // Show/hide text controls
    const textControls = this.dom.toolbar.querySelector('[data-text-controls]');
    if (textControls) {
      textControls.style.display = element.type === 'text' ? 'flex' : 'none';
    }

    // Update text inputs if text element
    if (element.type === 'text') {
      if (this.dom.textInput) this.dom.textInput.value = element.text;
      if (this.dom.fontSelect) this.dom.fontSelect.value = element.fontFamily;
      if (this.dom.fontSizeInput) this.dom.fontSizeInput.value = element.fontSize;
      if (this.dom.textColorInput) this.dom.textColorInput.value = element.color;
    }
  },

  hideToolbar() {
    this.dom.toolbar?.classList.remove('visible');
  },

  // =========================================================================
  // Mouse/Touch Handling
  // =========================================================================

  handleMouseDown(e) {
    const target = e.target;
    const elementWrapper = target.closest('.design-element');

    if (!elementWrapper) return;

    e.preventDefault();
    const elementId = elementWrapper.dataset.elementId;
    const element = this.state.elements.find(el => el.id === elementId);

    if (!element) return;

    this.selectElement(element);

    // Check if clicking a handle
    if (target.dataset.handle) {
      this.startResize(e, element, target.dataset.handle);
    } else if (target.dataset.rotate) {
      this.startRotate(e, element);
    } else {
      this.startDrag(e, element);
    }
  },

  handleMouseMove(e) {
    if (this.state.isDragging) {
      this.doDrag(e);
    } else if (this.state.isResizing) {
      this.doResize(e);
    } else if (this.state.isRotating) {
      this.doRotate(e);
    }
  },

  handleMouseUp(e) {
    this.state.isDragging = false;
    this.state.isResizing = false;
    this.state.isRotating = false;
  },

  // Touch handlers
  handleTouchStart(e) {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      const mouseEvent = new MouseEvent('mousedown', {
        clientX: touch.clientX,
        clientY: touch.clientY
      });
      this.handleMouseDown(mouseEvent);
    }
  },

  handleTouchMove(e) {
    if (e.touches.length === 1 && (this.state.isDragging || this.state.isResizing || this.state.isRotating)) {
      e.preventDefault();
      const touch = e.touches[0];
      const mouseEvent = new MouseEvent('mousemove', {
        clientX: touch.clientX,
        clientY: touch.clientY
      });
      this.handleMouseMove(mouseEvent);
    }
  },

  handleTouchEnd(e) {
    this.handleMouseUp(e);
  },

  // =========================================================================
  // Drag
  // =========================================================================

  startDrag(e, element) {
    this.state.isDragging = true;
    this.state.dragStart = { x: e.clientX, y: e.clientY };
    this.state.elementStart = { x: element.x, y: element.y };
  },

  doDrag(e) {
    const element = this.state.selectedElement;
    if (!element || !element.dom) return;

    const canvasRect = this.dom.designLayer.getBoundingClientRect();
    const deltaX = ((e.clientX - this.state.dragStart.x) / canvasRect.width) * 100;
    const deltaY = ((e.clientY - this.state.dragStart.y) / canvasRect.height) * 100;

    element.x = Math.max(5, Math.min(95, this.state.elementStart.x + deltaX));
    element.y = Math.max(5, Math.min(95, this.state.elementStart.y + deltaY));

    this.updateElementTransform(element);
  },

  // =========================================================================
  // Resize
  // =========================================================================

  startResize(e, element, handle) {
    this.state.isResizing = true;
    this.state.resizeHandle = handle;
    this.state.dragStart = { x: e.clientX, y: e.clientY };

    const content = element.dom.querySelector('img, .design-text');
    this.state.elementStart = {
      width: content?.offsetWidth || 100,
      height: content?.offsetHeight || 100,
      x: element.x,
      y: element.y
    };
  },

  doResize(e) {
    const element = this.state.selectedElement;
    if (!element || !element.dom) return;

    const content = element.dom.querySelector('img, .design-text');
    if (!content) return;

    const deltaX = e.clientX - this.state.dragStart.x;
    const deltaY = e.clientY - this.state.dragStart.y;
    const handle = this.state.resizeHandle;

    let newWidth = this.state.elementStart.width;
    let newHeight = this.state.elementStart.height;

    // Calculate new size based on handle
    if (handle.includes('e')) newWidth += deltaX;
    if (handle.includes('w')) newWidth -= deltaX;
    if (handle.includes('s')) newHeight += deltaY;
    if (handle.includes('n')) newHeight -= deltaY;

    // Maintain aspect ratio for images
    if (element.type === 'image') {
      const aspectRatio = this.state.elementStart.width / this.state.elementStart.height;
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        newHeight = newWidth / aspectRatio;
      } else {
        newWidth = newHeight * aspectRatio;
      }
    }

    // Apply minimum size
    newWidth = Math.max(30, newWidth);
    newHeight = Math.max(20, newHeight);

    if (element.type === 'image') {
      content.style.width = `${newWidth}px`;
      content.style.height = 'auto';
    } else {
      // For text, adjust font size
      const scale = newWidth / this.state.elementStart.width;
      element.fontSize = Math.max(10, Math.min(72, Math.round(element.fontSize * scale)));
      content.style.fontSize = `${element.fontSize}px`;
      this.state.elementStart.width = content.offsetWidth;
      if (this.dom.fontSizeInput) this.dom.fontSizeInput.value = element.fontSize;
    }
  },

  // =========================================================================
  // Rotate
  // =========================================================================

  startRotate(e, element) {
    this.state.isRotating = true;
    this.state.elementStart = { rotation: element.rotation };

    const rect = element.dom.getBoundingClientRect();
    this.state.rotateCenter = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    };

    this.state.startAngle = Math.atan2(
      e.clientY - this.state.rotateCenter.y,
      e.clientX - this.state.rotateCenter.x
    ) * (180 / Math.PI);
  },

  doRotate(e) {
    const element = this.state.selectedElement;
    if (!element) return;

    const currentAngle = Math.atan2(
      e.clientY - this.state.rotateCenter.y,
      e.clientX - this.state.rotateCenter.x
    ) * (180 / Math.PI);

    const deltaAngle = currentAngle - this.state.startAngle;
    element.rotation = this.state.elementStart.rotation + deltaAngle;

    // Snap to 0, 90, 180, 270 when close
    const snapAngles = [0, 90, 180, 270, -90, -180, -270];
    snapAngles.forEach(snap => {
      if (Math.abs(element.rotation - snap) < 5) {
        element.rotation = snap;
      }
    });

    this.updateElementTransform(element);
  },

  updateElementTransform(element) {
    if (!element.dom) return;
    element.dom.style.left = `${element.x}%`;
    element.dom.style.top = `${element.y}%`;
    element.dom.style.transform = `translate(-50%, -50%) rotate(${element.rotation}deg)`;
  },

  // =========================================================================
  // Element Actions
  // =========================================================================

  deleteSelectedElement() {
    const element = this.state.selectedElement;
    if (!element) return;

    element.dom?.remove();
    this.state.elements = this.state.elements.filter(el => el.id !== element.id);
    this.deselectAll();
  },

  duplicateSelectedElement() {
    const element = this.state.selectedElement;
    if (!element) return;

    const newElement = {
      ...element,
      id: 'el_' + Date.now(),
      x: element.x + 5,
      y: element.y + 5,
      dom: null
    };

    this.state.elements.push(newElement);
    this.renderElement(newElement);
    this.selectElement(newElement);
  },

  bringForward() {
    const element = this.state.selectedElement;
    if (!element) return;

    const maxZ = Math.max(...this.state.elements.map(el => el.zIndex));
    element.zIndex = maxZ + 1;
    if (element.dom) element.dom.style.zIndex = element.zIndex;
  },

  sendBackward() {
    const element = this.state.selectedElement;
    if (!element) return;

    const minZ = Math.min(...this.state.elements.map(el => el.zIndex));
    element.zIndex = Math.max(1, minZ - 1);
    if (element.dom) element.dom.style.zIndex = element.zIndex;
  },

  // =========================================================================
  // Text Editing
  // =========================================================================

  updateTextContent(text) {
    const element = this.state.selectedElement;
    if (!element || element.type !== 'text') return;

    element.text = text;
    const textEl = element.dom?.querySelector('.design-text');
    if (textEl) textEl.textContent = text;
  },

  updateTextFont(fontFamily) {
    const element = this.state.selectedElement;
    if (!element || element.type !== 'text') return;

    element.fontFamily = fontFamily;
    const textEl = element.dom?.querySelector('.design-text');
    if (textEl) textEl.style.fontFamily = fontFamily;
  },

  updateTextSize(size) {
    const element = this.state.selectedElement;
    if (!element || element.type !== 'text') return;

    element.fontSize = parseInt(size, 10);
    const textEl = element.dom?.querySelector('.design-text');
    if (textEl) textEl.style.fontSize = `${element.fontSize}px`;
  },

  updateTextColor(color) {
    const element = this.state.selectedElement;
    if (!element || element.type !== 'text') return;

    element.color = color;
    const textEl = element.dom?.querySelector('.design-text');
    if (textEl) textEl.style.color = color;
  },

  // =========================================================================
  // Keyboard Shortcuts
  // =========================================================================

  handleKeyDown(e) {
    if (!this.state.selectedElement) return;

    // Don't interfere with text input
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

    switch (e.key) {
      case 'Delete':
      case 'Backspace':
        e.preventDefault();
        this.deleteSelectedElement();
        break;
      case 'Escape':
        this.deselectAll();
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.nudgeElement(0, -1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        this.nudgeElement(0, 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        this.nudgeElement(-1, 0);
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.nudgeElement(1, 0);
        break;
    }
  },

  nudgeElement(dx, dy) {
    const element = this.state.selectedElement;
    if (!element) return;

    element.x = Math.max(5, Math.min(95, element.x + dx));
    element.y = Math.max(5, Math.min(95, element.y + dy));
    this.updateElementTransform(element);
  },

  // =========================================================================
  // Form Submission
  // =========================================================================

  handleFormSubmit(e) {
    // Serialize design data
    const designData = this.state.elements
      .filter(el => el.view) // Only include elements with a view
      .map(el => ({
        type: el.type,
        view: el.view,
        x: Math.round(el.x),
        y: Math.round(el.y),
        rotation: Math.round(el.rotation),
        ...(el.type === 'image' ? {
          src: el.cloudUrl || el.src,
          filename: el.filename
        } : {}),
        ...(el.type === 'text' ? {
          text: el.text,
          fontFamily: el.fontFamily,
          fontSize: el.fontSize,
          color: el.color
        } : {})
      }));

    // Store as JSON in hidden input
    if (this.dom.designDataInput) {
      this.dom.designDataInput.value = JSON.stringify(designData);
    }

    // Validate - require at least one design element
    if (designData.length === 0) {
      const requiresDesign = document.querySelector('[data-requires-design]');
      if (requiresDesign) {
        e.preventDefault();
        alert('Please add at least one logo or text to your design.');
        return;
      }
    }
  },

  // =========================================================================
  // Background Removal
  // =========================================================================

  showBgRemovalPanel(dataUrl, file) {
    if (!this.dom.bgRemovalPanel) return;

    // Store the original image data
    this.state.bgRemoval.originalDataUrl = dataUrl;
    this.state.bgRemoval.resultDataUrl = null;
    this.state.bgRemoval.pendingFile = file;

    // Show original preview
    if (this.dom.bgPreviewOriginal) {
      this.dom.bgPreviewOriginal.src = dataUrl;
    }

    // Reset UI state
    const afterPanel = this.dom.bgRemovalPanel.querySelector('.bg-removal-after');
    if (afterPanel) afterPanel.style.display = 'none';
    if (this.dom.bgProcessing) this.dom.bgProcessing.style.display = 'none';
    if (this.dom.bgResultActions) this.dom.bgResultActions.style.display = 'none';

    const actionsPanel = this.dom.bgRemovalPanel.querySelector('.bg-removal-actions');
    if (actionsPanel) actionsPanel.style.display = 'flex';

    // Show the panel
    this.dom.bgRemovalPanel.style.display = 'block';
  },

  closeBgRemovalPanel() {
    if (this.dom.bgRemovalPanel) {
      this.dom.bgRemovalPanel.style.display = 'none';
    }
    // Clear state
    this.state.bgRemoval.originalDataUrl = null;
    this.state.bgRemoval.resultDataUrl = null;
    this.state.bgRemoval.pendingFile = null;
    this.state.bgRemoval.isProcessing = false;
  },

  async removeBackground() {
    if (this.state.bgRemoval.isProcessing) return;
    if (!this.state.bgRemoval.originalDataUrl) return;

    this.state.bgRemoval.isProcessing = true;

    // Show processing state
    const actionsPanel = this.dom.bgRemovalPanel?.querySelector('.bg-removal-actions');
    if (actionsPanel) actionsPanel.style.display = 'none';
    if (this.dom.bgProcessing) this.dom.bgProcessing.style.display = 'flex';

    try {
      // Convert data URL to blob for processing
      const response = await fetch(this.state.bgRemoval.originalDataUrl);
      const blob = await response.blob();

      // Use @imgly/background-removal library (loaded from CDN)
      if (typeof removeBackground === 'function') {
        const resultBlob = await removeBackground(blob, {
          progress: (key, current, total) => {
            // Optional: Update progress UI
            console.log(`Background removal: ${key} ${current}/${total}`);
          }
        });

        // Convert result blob to data URL
        const resultDataUrl = await this.blobToDataUrl(resultBlob);
        this.state.bgRemoval.resultDataUrl = resultDataUrl;

        // Show result
        this.showBgRemovalResult(resultDataUrl);
      } else {
        // Fallback: If library not loaded, show error and use original
        console.warn('Background removal library not loaded');
        alert('Background removal feature is loading. Please try again in a moment.');
        this.resetBgRemovalPanel();
      }
    } catch (error) {
      console.error('Background removal error:', error);
      alert('Failed to remove background. Using original image.');
      this.resetBgRemovalPanel();
    }

    this.state.bgRemoval.isProcessing = false;
  },

  showBgRemovalResult(resultDataUrl) {
    // Show result preview
    if (this.dom.bgPreviewResult) {
      this.dom.bgPreviewResult.src = resultDataUrl;
    }

    const afterPanel = this.dom.bgRemovalPanel?.querySelector('.bg-removal-after');
    if (afterPanel) afterPanel.style.display = 'block';

    // Hide processing, show result actions
    if (this.dom.bgProcessing) this.dom.bgProcessing.style.display = 'none';
    if (this.dom.bgResultActions) this.dom.bgResultActions.style.display = 'flex';
  },

  resetBgRemovalPanel() {
    const actionsPanel = this.dom.bgRemovalPanel?.querySelector('.bg-removal-actions');
    if (actionsPanel) actionsPanel.style.display = 'flex';
    if (this.dom.bgProcessing) this.dom.bgProcessing.style.display = 'none';
  },

  useOriginalImage() {
    const dataUrl = this.state.bgRemoval.originalDataUrl;
    const file = this.state.bgRemoval.pendingFile;

    if (dataUrl) {
      this.addImageElement(dataUrl, file?.name || 'uploaded-image');
      if (file) this.uploadToCloudIfConfigured(file);
    }

    this.closeBgRemovalPanel();
  },

  async useResultImage() {
    const resultDataUrl = this.state.bgRemoval.resultDataUrl;

    if (resultDataUrl) {
      // Add the background-removed image to the canvas
      this.addImageElement(resultDataUrl, 'logo-transparent.png');

      // Upload the processed image to Cloudinary if configured
      if (this.config.cloudinary.cloudName && this.config.cloudinary.uploadPreset) {
        try {
          const blob = await this.dataUrlToBlob(resultDataUrl);
          const file = new File([blob], 'logo-transparent.png', { type: 'image/png' });
          const cloudUrl = await this.uploadToCloudinary(file);
          const element = this.state.elements[this.state.elements.length - 1];
          if (element) element.cloudUrl = cloudUrl;
        } catch (error) {
          console.error('Cloud upload error:', error);
        }
      }
    }

    this.closeBgRemovalPanel();
  },

  downloadResultImage() {
    const resultDataUrl = this.state.bgRemoval.resultDataUrl;
    if (!resultDataUrl) return;

    const link = document.createElement('a');
    link.href = resultDataUrl;
    link.download = 'logo-transparent.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  },

  dataUrlToBlob(dataUrl) {
    return fetch(dataUrl).then(res => res.blob());
  },

  // =========================================================================
  // Utility
  // =========================================================================

  getDesignPreviewImage() {
    // Could use html2canvas here for a preview image
    return null;
  },

  /**
   * Serialize editor state into hidden form fields so cart receives all customization data.
   * Called before add-to-cart to ensure _design_data, _logo_url, _logo_filename, _logo_placement are populated.
   */
  serializeToHiddenFields() {
    // Helper: check if a string is a data URL (base64) - these are too large for Shopify properties
    const isDataUrl = (str) => str && str.startsWith('data:');

    // 1. Serialize design data - exclude base64 src to avoid Shopify's ~4KB property limit
    const designData = this.state.elements
      .filter(el => el.view)
      .map(el => ({
        type: el.type,
        view: el.view,
        x: Math.round(el.x),
        y: Math.round(el.y),
        rotation: Math.round(el.rotation),
        ...(el.type === 'image' ? {
          src: el.cloudUrl || (isDataUrl(el.src) ? '[uploaded]' : el.src),
          cloudUrl: el.cloudUrl || '',
          filename: el.filename
        } : {}),
        ...(el.type === 'text' ? {
          text: el.text,
          fontFamily: el.fontFamily,
          fontSize: el.fontSize,
          color: el.color
        } : {})
      }));

    if (this.dom.designDataInput) {
      this.dom.designDataInput.value = designData.length > 0 ? JSON.stringify(designData) : '';
    }

    // 2. Find the first image element to populate logo fields
    const firstImage = this.state.elements.find(el => el.type === 'image');

    const logoUrlInput = document.querySelector('[name="properties[_logo_url]"]');
    const logoFilenameInput = document.querySelector('[name="properties[_logo_filename]"]');
    const logoPlacementInput = document.querySelector('[name="properties[_logo_placement]"]');

    if (firstImage) {
      // Only set _logo_url if it's a real CDN URL (Cloudinary), not a data URL
      const logoUrl = firstImage.cloudUrl || '';
      if (logoUrlInput) logoUrlInput.value = (!isDataUrl(logoUrl)) ? logoUrl : '';
      if (logoFilenameInput) logoFilenameInput.value = firstImage.filename || '';
      if (logoPlacementInput) logoPlacementInput.value = firstImage.view || this.state.currentView || '';
    } else {
      if (logoUrlInput) logoUrlInput.value = '';
      if (logoFilenameInput) logoFilenameInput.value = '';
      if (logoPlacementInput) logoPlacementInput.value = '';
    }
  }
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => ProductEditor.init());
} else {
  ProductEditor.init();
}

// Wishlist Functionality
const Wishlist = {
  storageKey: 'uniformic_wishlist',
  toastTimer: null,

  init() {
    this.buttons = document.querySelectorAll('[data-wishlist-btn]');
    this.toast = document.querySelector('[data-wishlist-toast]');
    this.toastText = document.querySelector('[data-wishlist-toast-text]');
    if (!this.buttons.length) return;

    this.buttons.forEach(btn => {
      const productId = btn.dataset.productId;
      if (this.isInWishlist(productId)) {
        btn.classList.add('active');
        var textEl = btn.querySelector('.action-btn__text') || btn.querySelector('span');
        if (textEl) textEl.textContent = 'In Wishlist';
      }
      btn.addEventListener('click', (e) => this.toggle(e.currentTarget));
    });
  },

  getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey)) || [];
    } catch {
      return [];
    }
  },

  saveWishlist(items) {
    localStorage.setItem(this.storageKey, JSON.stringify(items));
  },

  isInWishlist(productId) {
    return this.getWishlist().includes(String(productId));
  },

  syncButtons(productId, isActive) {
    // Sync all wishlist buttons for the same product
    document.querySelectorAll('[data-wishlist-btn][data-product-id="' + productId + '"]').forEach(function(btn) {
      var textEl = btn.querySelector('.action-btn__text') || btn.querySelector('span');
      if (isActive) {
        btn.classList.add('active');
        if (textEl) textEl.textContent = 'In Wishlist';
      } else {
        btn.classList.remove('active');
        if (textEl) textEl.textContent = 'Add to Wishlist';
      }
    });
  },

  showToast(added) {
    if (!this.toast || !this.toastText) return;
    var heartSvg = this.toast.querySelector('svg');

    this.toastText.textContent = added ? 'Added to wishlist' : 'Removed from wishlist';

    if (heartSvg) {
      heartSvg.classList.remove('toast-heart', 'toast-remove');
      heartSvg.classList.add(added ? 'toast-heart' : 'toast-remove');
    }

    // Show toast
    this.toast.classList.add('show');

    // Auto-hide after 2.5s
    if (this.toastTimer) clearTimeout(this.toastTimer);
    var self = this;
    this.toastTimer = setTimeout(function() {
      self.toast.classList.remove('show');
    }, 2500);
  },

  toggle(btn) {
    var productId = String(btn.dataset.productId);
    var wishlist = this.getWishlist();
    var added;

    if (wishlist.includes(productId)) {
      wishlist = wishlist.filter(function(id) { return id !== productId; });
      added = false;
    } else {
      wishlist.push(productId);
      added = true;
    }

    this.saveWishlist(wishlist);
    this.syncButtons(productId, added);

    // Heart pop animation
    btn.classList.remove('animating', 'burst');
    void btn.offsetWidth; // force reflow
    btn.classList.add('animating');
    if (added) btn.classList.add('burst');

    setTimeout(function() {
      btn.classList.remove('animating', 'burst');
    }, 600);

    // Toast notification
    this.showToast(added);

    // Dispatch custom event for other components
    document.dispatchEvent(new CustomEvent('wishlist:updated', {
      detail: { productId: productId, wishlist: wishlist, added: added }
    }));
  }
};

// Initialize Wishlist
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() { Wishlist.init(); });
} else {
  Wishlist.init();
}
